import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, RefreshCw, Loader2, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import {
 loadNotificationsFn,
 loadListingNamesFn,
 loadNotificationTypesFn,
} from "./-dashboard.admin.notifications.functions";

export const Route = createFileRoute("/dashboard/admin/notifications")({
 component: NotificationsPage,
});

interface Row {
 id: string;
 listing_id: string | null;
 recipient_email: string;
 notification_type: string;
 subject: string;
 send_status: string;
 resend_message_id: string | null;
 error_message: string | null;
 sent_at: string | null;
 created_at: string;
}

const STATUSES = ["sent", "failed", "blocked", "pending", "suppressed"] as const;
const RANGES = [
 { key: "24h", label: "24h", hours: 24 },
 { key: "7d", label: "7 days", hours: 24 * 7 },
 { key: "30d", label: "30 days", hours: 24 * 30 },
 { key: "all", label: "All time", hours: 0 },
] as const;

const PAGE_SIZE = 50;
type Cursor = { created_at: string; id: string } | null;

function NotificationsPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const [rows, setRows] = useState<Row[]>([]);
 const [listings, setListings] = useState<Record<string, string>>({});
 const [busy, setBusy] = useState(false);
 const [errorMsg, setErrorMsg] = useState<string | null>(null);

 const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("7d");
 const [type, setType] = useState<string>("all");
 const [status, setStatus] = useState<string>("all");
 const [q, setQ] = useState("");
 const [debouncedQ, setDebouncedQ] = useState("");
 const [typeOptions, setTypeOptions] = useState<string[]>([]);

 const [cursorStack, setCursorStack] = useState<Cursor[]>([null]);
 const [pageIndex, setPageIndex] = useState(0);
 const [hasMore, setHasMore] = useState(false);
 const reqIdRef = useRef(0);

 useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 400); return () => clearTimeout(t); }, [q]);
 useEffect(() => { setCursorStack([null]); setPageIndex(0); }, [range, type, status, debouncedQ]);

 const sinceISO = useMemo(() => {
 const r = RANGES.find((x) => x.key === range)!;
 return r.hours ? new Date(Date.now() - r.hours * 3600 * 1000).toISOString() : null;
 }, [range]);

 const reload = useCallback(async () => {
 const myReq = ++reqIdRef.current;
 setBusy(true);
 setErrorMsg(null);
 try {
 const cursor = cursorStack[pageIndex] ?? null;
 // NOTE: no count:"exact" — cursor pagination + indexed filters only.
 const data = await loadNotificationsFn({
 data: {
 limit: PAGE_SIZE + 1,
 cursor,
 sinceISO,
 type,
 status,
 search: debouncedQ,
 },
 });
 if (myReq !== reqIdRef.current) return;
 const fetched = (data ?? []) as Row[];
 setHasMore(fetched.length > PAGE_SIZE);
 setRows(fetched.slice(0, PAGE_SIZE));

 // Resolve listing names for the loaded page only
 const ids = Array.from(new Set(fetched.map((r) => r.listing_id).filter((x): x is string => !!x)));
 if (ids.length) {
 const ls = await loadListingNamesFn({ data: { ids } });
 const map: Record<string, string> = {};
 (ls ?? []).forEach((l: any) => (map[l.id] = l.practice));
 if (myReq === reqIdRef.current) setListings(map);
 } else if (myReq === reqIdRef.current) setListings({});
 } catch (e: any) {
 if (myReq !== reqIdRef.current) return;
 setErrorMsg(e?.message ?? "Failed to load");
 setRows([]); setHasMore(false);
 } finally {
 if (myReq === reqIdRef.current) setBusy(false);
 }
 }, [cursorStack, pageIndex, sinceISO, type, status, debouncedQ]);

 const loadTypes = async () => {
 const data = await loadNotificationTypesFn();
 const set = new Set<string>();
 (data ?? []).forEach((r: any) => r.notification_type && set.add(r.notification_type));
 setTypeOptions(Array.from(set).sort());
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole === "admin") reload();
 }, [user, loading, primaryRole, reload]);

 useEffect(() => { if (primaryRole === "admin") loadTypes(); }, [primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

 const badge = (s: string) =>
 s === "sent" ? "border-primary text-primary"
 : s === "blocked" || s === "suppressed" ? "border-yellow-500 text-yellow-600"
 : s === "pending" ? "border-border text-muted-foreground"
 : "border-destructive text-destructive";

 const clearFilters = () => { setRange("7d"); setType("all"); setStatus("all"); setQ(""); };

 const goNext = () => {
 if (!hasMore || rows.length === 0) return;
 const last = rows[rows.length - 1];
 const next: Cursor = { created_at: last.created_at, id: last.id };
 setCursorStack((s) => { const c = s.slice(0, pageIndex + 1); c.push(next); return c; });
 setPageIndex((p) => p + 1);
 };
 const goPrev = () => { if (pageIndex > 0) setPageIndex((p) => p - 1); };
 const showingFrom = rows.length === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
 const showingTo = pageIndex * PAGE_SIZE + rows.length;

 return (
 <AdminPageShell
   title="Listing Notifications"
   description="Delivery status for emails sent from the listing pipeline."
   primaryAction={{ label: busy ? "Refreshing…" : "Refresh", icon: RefreshCw, onClick: reload }}
 >
 <div className="space-y-5">
 <section className="border border-border bg-card p-4 space-y-3">
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Range</span>
 {RANGES.map((r) => (
 <button key={r.key} onClick={() => setRange(r.key)}
 className={`text-xs px-2.5 py-1 border ${range === r.key ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
 {r.label}
 </button>
 ))}
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Status</span>
 <button onClick={() => setStatus("all")} className={`text-xs px-2.5 py-1 border ${status === "all" ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>All</button>
 {STATUSES.map((s) => (
 <button key={s} onClick={() => setStatus(s)}
 className={`text-xs px-2.5 py-1 border capitalize ${status === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
 {s}
 </button>
 ))}
 </div>
 <div className="flex flex-wrap items-center gap-2">
 <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Type</span>
 <select value={type} onChange={(e) => setType(e.target.value)}
 className="h-8 text-xs px-2 bg-background border border-border rounded-md">
 <option value="all">All types ({typeOptions.length})</option>
 {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
 </select>
 <div className="relative flex-1 min-w-[220px]">
 <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
 <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or subject…" className="h-8 pl-7 text-xs" />
 </div>
 {(range !== "7d" || type !== "all" || status !== "all" || q) && (
 <Button size="sm" variant="ghost" onClick={clearFilters}><X className="h-3 w-3" /> Clear</Button>
 )}
 </div>
 </section>

 <section>
 <div className="border border-border bg-card overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-muted/40 text-muted-foreground">
 <tr className="text-left">
 <th className="px-3 py-2 font-medium">When</th>
 <th className="px-3 py-2 font-medium">Listing</th>
 <th className="px-3 py-2 font-medium">Type</th>
 <th className="px-3 py-2 font-medium">Recipient</th>
 <th className="px-3 py-2 font-medium">Subject</th>
 <th className="px-3 py-2 font-medium">Status</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r) => (
 <tr key={r.id} className="border-t border-border align-top">
 <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
 <td className="px-3 py-2">{(r.listing_id && listings[r.listing_id]) || <span className="text-muted-foreground">—</span>}</td>
 <td className="px-3 py-2"><code className="text-[11px]">{r.notification_type}</code></td>
 <td className="px-3 py-2">{r.recipient_email}</td>
 <td className="px-3 py-2">
 <p className="truncate max-w-[260px]">{r.subject}</p>
 {r.error_message && <p className="text-muted-foreground truncate max-w-[260px]">{r.error_message}</p>}
 </td>
 <td className="px-3 py-2"><span className={`uppercase tracking-wider px-2 py-0.5 border ${badge(r.send_status)}`}>{r.send_status}</span></td>
 </tr>
 ))}
 {!busy && rows.length === 0 && (
 <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No notifications match these filters.</td></tr>
 )}
 {busy && (
 <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</td></tr>
 )}
 </tbody>
 </table>
 </div>
 {errorMsg && (
 <p className="mt-2 text-xs text-destructive">{errorMsg} <button className="underline ml-2" onClick={reload}>Retry</button></p>
 )}
 <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-xs text-muted-foreground">
 <div>Showing {showingFrom}–{showingTo}{hasMore ? " · more available" : ""}</div>
 <div className="flex items-center gap-1">
 <Button size="sm" variant="outline" disabled={pageIndex === 0 || busy} onClick={goPrev}><ChevronLeft className="h-3 w-3" /></Button>
 <span className="px-2">Page {pageIndex + 1}</span>
 <Button size="sm" variant="outline" disabled={!hasMore || busy} onClick={goNext}><ChevronRight className="h-3 w-3" /></Button>
 </div>
 </div>
 </section>
 </div>
 </AdminPageShell>
 );
}
